import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronUp, Timer } from 'lucide-react';
import { DollarCircleIcon, PieCircleIcon } from './PortfolioCardIcons';
import { Badge, Card, EmptyCircle, Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';
import { usePredictionMarketEventsFindById } from '@/lib/endpoints';
import { resolvePositionLeg } from '@/lib/trading/position';
import { legAnswerLabel } from '@/lib/trading/position-label';
import { fmtUsd, signedUsd } from '@/lib/trading/format';
import { formatClock } from '@/lib/snake-market';
import { formatEventDate, formatWindowRange, type EventPositionGroup } from '@/lib/trading/portfolio-groups';
import { useCapabilityEnabled, KILL_SWITCH } from '@/lib/launchdarkly/killSwitches';
import type { SellPositionContext } from '../BinaryMarkets/SellPopup';
import { MarketOutcomePricesSubscriber, targetCents, useOutcomePrices } from '../BinaryMarkets/useOutcomePrices';
import { useNowMs } from '../SnakeMarkets/useNowMs';

/** Only events closing within this window get the urgency countdown pill. */
const COUNTDOWN_WINDOW_MS = 60 * 60 * 1000;

export interface EventPositionsCardProps {
  /** The event and the user's open positions under it. */
  group: EventPositionGroup;
  /** Open the sell sheet for one resolved position. */
  onSell: (context: SellPositionContext) => void;
  className?: string;
}

/**
 * Portfolio Positions card (Figma 1769:91105) — one EVENT with the user's open
 * positions expandable inside it: category line + countdown pill, question +
 * close date, "Your Pick", Total Value / Total Positions stats, and an
 * "N Open Positions" section whose rows carry Answer · Avg Price · Value ·
 * **Sell**.
 *
 * Market context (option legs for the sell sheet, answer labels, live-price
 * channels) is fetched from `/prediction-market/events/:eventId` on mount so
 * the "Value" badge and totals track the live mark price BEFORE the drawer is
 * ever opened (they'd otherwise sit on the stale mirror snapshot); until it
 * lands the rows render from the snapshot with Sell disabled.
 */
export function EventPositionsCard({ group, onSell, className }: EventPositionsCardProps) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const nowMs = useNowMs();
  const { prices, setPrice } = useOutcomePrices();
  const tradingEnabled = useCapabilityEnabled(KILL_SWITCH.TRADING_ORDER_PLACEMENT);

  const { data: event } = usePredictionMarketEventsFindById(group.eventId ?? '', {
    query: { enabled: Boolean(group.eventId) },
  });
  const options = useMemo(() => (event?.markets ?? []).flatMap((m) => m.options), [event]);

  // Distinct market channels for the live-price overlay.
  const channels = useMemo(() => {
    const byMarket = new Map<string, string>();
    for (const o of options) byMarket.set(o.marketId, o.externalMarketId);
    return byMarket;
  }, [options]);

  // Project each position to its answer label + live value/P&L (falling back
  // to the mirror snapshot while the event detail / live prices are pending).
  const rows = group.positions.map((p) => {
    const leg = options.length ? resolvePositionLeg(p.outcomeId, p.side, options) : null;
    const cents = leg ? targetCents(leg.target, prices) : null;
    const value = cents != null ? (p.quantity * cents) / 100 : (p.cost ?? 0) + (p.unrealizedPnl ?? 0);
    const pnl = cents != null && p.avgEntryPrice != null ? value - p.quantity * p.avgEntryPrice : (p.unrealizedPnl ?? 0);
    const label = leg ? legAnswerLabel(leg) : (p.outcomeName ?? (p.side === 'LONG' ? 'Yes' : 'No'));
    const entryCents = p.avgEntryPrice != null ? Math.round(p.avgEntryPrice * 100) : null;
    return { position: p, leg, label, value, pnl, entryCents };
  });
  const totalPnl = rows.reduce((sum, r) => sum + r.pnl, 0);
  // Live card total — each row already falls back to the mirror snapshot, so
  // this equals group.totalValue until the first price tick lands.
  const totalValue = rows.reduce((sum, r) => sum + r.value, 0);

  const closeMs = group.closesAt ? new Date(group.closesAt).getTime() : NaN;
  const remainingMs = closeMs - nowMs;
  const showTimer = !Number.isNaN(closeMs) && remainingMs > 0 && remainingMs <= COUNTDOWN_WINDOW_MS;

  // "Your Pick: Claude" — up to two answer labels, then a +N overflow.
  const pickLabels = rows.map((r) => r.label);
  const yourPick = pickLabels.slice(0, 2).join(', ') + (pickLabels.length > 2 ? ` +${pickLabels.length - 2}` : '');

  return (
    <Card
      variant="default"
      className={cn(
        'relative flex flex-col gap-3 rounded-[12px] border-stroke-soft p-3.5 shadow-[0_0_4px_0_var(--stroke-soft)]',
        className,
      )}
    >
      {[...channels].map(([marketId, externalMarketId]) => (
        <MarketOutcomePricesSubscriber
          key={marketId}
          marketId={marketId}
          externalMarketId={externalMarketId}
          onPrice={setPrice}
        />
      ))}

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <EmptyCircle src={group.imageUrl} alt={group.title} size="sm" className="size-9" />
          <span className="truncate text-base font-normal text-purple">{group.category ?? 'Prediction'}</span>
        </div>
        {showTimer && (
          <div className="flex h-[30px] shrink-0 items-center gap-1 rounded-pill bg-warning-soft px-2.5">
            <Timer className="size-3.5 text-warning" aria-hidden />
            <span className="text-base font-semibold text-warning tabular-nums tracking-[-0.32px]">
              {formatClock(group.closesAt, nowMs)}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1 px-4">
        <h3 className="text-lg font-semibold text-ink leading-snug tracking-[-0.36px]">{group.title}</h3>
        {group.closesAt && (
          <p className="text-base font-light text-ink tracking-[-0.32px]">
            {group.recurring ? formatWindowRange(group.opensAt, group.closesAt) : formatEventDate(group.closesAt)}
          </p>
        )}
        <div className="flex items-center gap-1 text-base font-semibold tracking-[-0.32px]">
          <span className="text-ink">Your Pick:</span>
          <span className="truncate text-brand-grad-deep">{yourPick}</span>
        </div>
      </div>

      <div className="grid h-[50px] grid-cols-[1fr_1px_1fr] items-center rounded-pill bg-surface-soft px-2">
        <div className="flex min-w-0 items-center justify-center gap-1.5">
          <DollarCircleIcon />
          <div className="flex min-w-0 flex-col">
            <span className="whitespace-nowrap text-sm font-normal text-ink tracking-[-0.28px]">Total Value</span>
            <span className="truncate text-base font-semibold text-ink tracking-[-0.32px]">{fmtUsd(totalValue)}</span>
          </div>
        </div>
        <div className="h-[34px] w-px bg-border" aria-hidden />
        <div className="flex min-w-0 items-center justify-center gap-1.5">
          <PieCircleIcon />
          <div className="flex min-w-0 flex-col">
            <span className="whitespace-nowrap text-sm font-normal text-ink tracking-[-0.28px]">Total Positions</span>
            <span className="truncate text-base font-semibold text-ink tracking-[-0.32px]">{group.totalContracts}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3.5 rounded-[12px] border-[1.5px] border-[#f3f1f5] bg-surface p-4">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="group flex w-full cursor-pointer items-center justify-between gap-2"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg font-medium text-ink">
              {group.positions.length} Open Position{group.positions.length === 1 ? '' : 's'}
            </span>
            <Badge
              variant={totalPnl >= 0 ? 'success' : 'danger'}
              className={cn(
                'h-[21px] rounded-pill px-2.5 py-0.5 text-sm font-medium normal-case tracking-[-0.28px]',
                totalPnl >= 0 ? 'bg-up/10 text-up' : 'bg-down/10 text-down',
              )}
            >
              Value {signedUsd(totalPnl)}
            </Badge>
          </div>
          {expanded ? (
            <ChevronUp className="size-4 text-ink" aria-hidden />
          ) : (
            <ChevronDown className="size-4 text-ink" aria-hidden />
          )}
        </button>

        {expanded && (
          <div className="flex flex-col gap-2">
            {/* Header shares the row grid (plus the tiles' 16px padding) so the
                labels sit centred over their columns at every width. */}
            <div className={cn(POSITION_ROW_GRID, 'px-4 text-sm text-ink-muted/70 tracking-[-0.28px]')}>
              <span>Answer</span>
              <span className="whitespace-nowrap text-center">Avg Price</span>
              <span className="text-center">Value</span>
              <span aria-hidden />
            </div>
            <div className="-mt-1 h-px bg-border" />
            {rows.map((row, i) => (
              <div key={row.position.positionId} className="flex flex-col gap-2 rounded-[12px] bg-surface-soft p-4">
                <div className={cn(POSITION_ROW_GRID, 'text-base font-medium')}>
                  <span className={cn('truncate', ROW_COLORS[i % ROW_COLORS.length])}>{row.label}</span>
                  <span className="text-center text-ink tabular-nums tracking-[-0.32px]">
                    {row.entryCents != null ? `${row.entryCents}¢` : '—'}
                  </span>
                  <span className="min-w-0 truncate text-center text-ink tabular-nums tracking-[-0.32px]">
                    {fmtUsd(row.value)}
                  </span>
                  <button
                    type="button"
                    onClick={() => row.leg && onSell({ position: row.position, leg: row.leg, label: row.label })}
                    disabled={!tradingEnabled || !row.leg}
                    className="h-7 w-full rounded-pill bg-purple text-base font-semibold capitalize text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Sell
                  </button>
                </div>
                <div className="h-px w-full bg-border" aria-hidden />
                <div className="flex items-end justify-between">
                  <span className="text-xs text-ink-muted tracking-[-0.24px]">
                    {row.position.quantity} Contract{row.position.quantity === 1 ? '' : 's'}
                  </span>
                  <ChevronDown className="size-4 text-ink" aria-hidden />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(group.recurring ? group.instrument : group.eventId) && (
        <button
          type="button"
          onClick={() =>
            navigate(
              group.recurring
                ? `/snake/${group.instrument}${group.subCategoryCode ? `/${group.subCategoryCode}` : ''}`
                : `/events/${group.eventId}`,
            )
          }
          className="flex cursor-pointer items-center justify-center gap-1 self-end text-base font-semibold text-purple underline tracking-[-0.32px] transition-colors hover:text-brand-grad-deep"
        >
          <span>Go to Market</span>
          <ChevronRight className="size-4" aria-hidden />
        </button>
      )}
    </Card>
  );
}

/** Per-row answer colours, cycled in Figma order (purple → blue → green → orange). */
const ROW_COLORS = ['text-brand-grad-deep', 'text-verified', 'text-up', 'text-warning'] as const;

/**
 * Shared column grid for the expanded rows AND their header: flexible answer,
 * price/value columns sized to content (centred), fixed Sell width. Keeps the
 * header labels centred over the values and stops the value colliding with
 * Sell on narrow screens.
 */
const POSITION_ROW_GRID = 'grid grid-cols-[minmax(64px,1.2fr)_minmax(44px,auto)_minmax(52px,1fr)_95px] items-center gap-2';

/**
 * Loading placeholder mirroring an {@link EventPositionsCard}: category row +
 * timer pill, title, date, pick line, the two-cell totals box, the collapsed
 * open-positions bar, and the Go to Market link.
 */
export function EventPositionsCardSkeleton() {
  return (
    <Card variant="default" className="flex flex-col gap-3 p-3.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Skeleton className="size-[30px] shrink-0 rounded-full" />
          <Skeleton className="h-4 w-36 rounded" />
        </div>
        <Skeleton className="h-6 w-20 rounded-pill" />
      </div>
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-5 w-3/4 rounded" />
        <Skeleton className="h-4 w-32 rounded" />
        <Skeleton className="mt-1 h-4 w-40 rounded" />
      </div>
      <div className="flex items-stretch gap-2 rounded-card border border-border bg-surface-muted/60 px-3.5 py-2.5">
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton className="h-3 w-16 rounded" />
          <Skeleton className="h-5 w-14 rounded" />
        </div>
        <div className="w-px self-stretch bg-border" aria-hidden />
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton className="h-3 w-20 rounded" />
          <Skeleton className="h-5 w-10 rounded" />
        </div>
      </div>
      <Skeleton className="h-11 w-full rounded-card" />
      <Skeleton className="h-4 w-24 rounded" />
    </Card>
  );
}
